import { Crown, HelpCircle, Lightbulb, Sparkles } from 'lucide-react';
// LIL-359: Search bar hidden for now. Restore `Search`, `X` imports when re-enabling.
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { authClient } from '@/lib/auth-client';
import { useTourStore, useTooltipStore, usePlanStore } from '@/stores';
import { isSubscriptionActive } from '@/lib/subscription';

interface TopBarProps {
  onNavigateToProfile?: () => void;
  onNavigateToBilling?: () => void;
}

export function TopBar({ onNavigateToProfile, onNavigateToBilling }: TopBarProps) {
  const { data: session } = authClient.useSession();
  const resetTour = useTourStore(s => s.reset);
  const tipsEnabled = useTooltipStore(s => s.tipsEnabled);
  const toggleTips = useTooltipStore(s => s.toggleTips);
  // LIL-359: Search bar hidden for now. Restore these when re-enabling:
  // const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  // const [searchQuery, setSearchQuery] = useState('');

  // Subscribed users see their plan (and a route to manage it) instead of an
  // "Upgrade" CTA. Wait for the plan store to hydrate so we never flash the
  // wrong action at a paying user.
  const subscription = usePlanStore(s => s.subscription);
  const planHydrated = usePlanStore(s => s.hydrated);
  const hasActivePlan = isSubscriptionActive(subscription);
  const planLabel = subscription?.isLifetime ? 'Lifetime' : (subscription?.planName ?? '');

  const userInitials = session?.user?.name
    ? session.user.name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '?';

  // LIL-359: Search bar hidden for now. Restore when re-enabling:
  // const handleSearchSubmit = (e: React.FormEvent) => {
  //   e.preventDefault();
  //   // TODO: wire up search once the backend endpoint lands.
  // };

  return (
    <header className="sticky top-0 z-30 bg-white/70 backdrop-blur-2xl border-b border-white/20 shadow-sm">
      <div className="flex items-center justify-between gap-4 px-4 md:px-8 py-3">
        {/* Left side - Search (LIL-359: hidden for now; spacer keeps actions right-aligned) */}
        <div className="flex-1 max-w-2xl" />
        {/*
        <div className="flex-1 max-w-2xl">
          {isSearchExpanded ? (
            <form onSubmit={handleSearchSubmit} className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search content, topics, analytics..."
                className="w-full pl-10 pr-10 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-300 transition-all"
                autoFocus
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setIsSearchExpanded(false);
                  setSearchQuery('');
                }}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 hover:bg-gray-200"
              >
                <X className="size-4 text-gray-500" />
              </Button>
            </form>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsSearchExpanded(true)}
                  className="text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                >
                  <Search className="size-4 mr-2" />
                  Search
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" accentSide="top">
                Search across content, topics, and analytics
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        */}

        {/* Right side - Actions */}
        <div className="flex items-center gap-2 md:gap-3">
          {/* Show tips pill (LIL-377) — opt-in descriptive hover tooltips */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={toggleTips}
                aria-pressed={tipsEnabled}
                className={`hidden md:flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${
                  tipsEnabled
                    ? 'border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Lightbulb className="size-3.5 flex-shrink-0" />
                <span className="whitespace-nowrap">Show tips</span>
                <span
                  className={`size-1.5 flex-shrink-0 rounded-full ${
                    tipsEnabled ? 'bg-purple-500' : 'bg-gray-300'
                  }`}
                />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" accentSide="top">
              {tipsEnabled
                ? 'Hover tips are on. Click to hide.'
                : 'Hover tips are off. Click to show.'}
            </TooltipContent>
          </Tooltip>

          {/* Restart tour */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={resetTour}
                data-tour="restart-tour"
                aria-label="Restart product tour"
                className="p-1.5 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              >
                <HelpCircle className="size-5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" accentSide="top">
              Restart product tour
            </TooltipContent>
          </Tooltip>

          {/* Plan pill for subscribed users, Upgrade CTA otherwise. Hidden until
              the plan store hydrates so a paying user never sees "Upgrade". */}
          {planHydrated &&
            (hasActivePlan ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className="hidden md:flex border-purple-200 text-purple-700 hover:bg-purple-50 hover:text-purple-800"
                    onClick={onNavigateToBilling}
                  >
                    <Crown className="size-4 mr-1.5 text-purple-600" />
                    {planLabel}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" accentSide="top">
                  Manage your {planLabel} plan
                </TooltipContent>
              </Tooltip>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-sm hidden md:flex"
                    onClick={onNavigateToBilling}
                  >
                    <Sparkles className="size-4 mr-1.5" />
                    Upgrade
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" accentSide="top">
                  Unlock higher limits and premium features
                </TooltipContent>
              </Tooltip>
            ))}

          {/* Profile - Only picture, clickable to profile page */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={onNavigateToProfile}
                className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="View profile"
              >
                <Avatar className="size-8 shadow-sm">
                  {session?.user?.image && (
                    <AvatarImage src={session.user.image} alt={session?.user?.name || 'User'} />
                  )}
                  <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-xs font-medium">
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" accentSide="top">
              View profile & settings
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </header>
  );
}
